// ═══════════════════════════════════════════════════════════════════
// PROJECTS DETAIL DATA - Extended data for Case Studies
// ═══════════════════════════════════════════════════════════════════

import { NikoProjectExtended } from '../types/project';

export const projectsDetailData: NikoProjectExtended[] = [
  {
    id: 'neowispr',
    title: 'Native AI Dictation für macOS',
    company: 'NeoWispr',
    role: 'Founder, Product Engineer & UX Lead',
    period: '2026 – Heute',
    description:
      'Entwicklung einer nativen macOS-Diktierapp mit Offline-STT, Live-Übersetzung, optionalem Cleanup und eigener Begriffspflege.',
    tech: ['Swift', 'Whisper', 'macOS', 'TypeScript', 'AI Agents'],
    industry: 'Neon Product Lab',
    current: true,
    overview:
      'NeoWispr ist eine native macOS-App für Diktat und Sprachsteuerung. Der Fokus liegt auf schneller Eingabe, lokalen Modellen, sauberer Mac-Integration und einem Workflow, der in jeder App funktioniert.',
    challenges: [
      {
        title: 'Native statt Web-Demo',
        description:
          'Diktat muss sich auf macOS wie ein Systemwerkzeug anfühlen, nicht wie ein weiterer Tab.',
      },
      {
        title: 'Privatsphäre und Geschwindigkeit',
        description:
          'Spracherkennung soll lokal und schnell laufen, ohne jedes Wort unnötig in die Cloud zu schicken.',
      },
      {
        title: 'Echte Alltagssprache',
        description:
          'Eigene Begriffe, Produktnamen und kurze Korrekturen müssen im normalen Schreibfluss funktionieren.',
      },
    ],
    solutions: [
      {
        title: 'Mac-native Eingabe',
        description:
          'Globaler Shortcut, systemweite Texteingabe und eine Oberfläche, die den Schreibfluss nicht unterbricht.',
        tech: ['Swift', 'macOS', 'Accessibility'],
      },
      {
        title: 'Lokale STT-Pipeline',
        description:
          'Offline-Diktat mit optionalem Cleanup für bessere Texte, Übersetzung und formatierte Ausgaben.',
        tech: ['Whisper', 'Apple Silicon', 'Text Cleanup'],
      },
      {
        title: 'Produktiver Wörter-Kontext',
        description:
          'Custom Words und Snippets für Namen, Marken, Fachbegriffe und wiederkehrende Formulierungen.',
        tech: ['Dictionary', 'Snippets', 'User Context'],
      },
    ],
    results: [
      { metric: 'Shortcut', value: '⌥ Space', description: 'Schneller Einstieg ohne App-Wechsel' },
      { metric: 'Privacy', value: 'Local-first', description: 'Diktat auf Apple Silicon' },
      { metric: 'Beta', value: 'Live', description: 'Testbar als echte macOS-App' },
    ],
    duration: 'laufend',
    teamSize: 'Founder-led',
    deliverables: ['macOS App', 'Offline Dictation', 'Live Translation', 'Custom Words'],
    nextProject: 'neovoice',
    prevProject: 'valsight',
  },
  {
    id: 'neovoice',
    title: 'Voice Layer für lokale AI-Flows',
    company: 'NeoVoice',
    role: 'Founder, Voice UX & Runtime Engineering',
    period: '2026 – Heute',
    description:
      'Voice-Assistant-Schicht für schnelle Gespräche, lokale Audio-Flows, Interruptions und eine natürlichere Steuerung von AI-Agenten.',
    tech: ['Swift', 'Audio Routing', 'Realtime', 'macOS', 'AI Agents'],
    industry: 'Neon Product Lab',
    current: true,
    overview:
      'NeoVoice ist die Voice-Schicht der Neon-Produktfamilie. Ziel ist ein Agent, der nicht nur Text annimmt, sondern natürlich zuhört, spricht, unterbrochen werden kann und lokale Mac-Flows sauber nutzt.',
    challenges: [
      {
        title: 'Natürliches Turn-Taking',
        description:
          'Voice-Flows müssen Unterbrechungen, kurze Antworten und schnelle Kontextwechsel aushalten.',
      },
      {
        title: 'Audio-Input richtig routen',
        description:
          'Mikrofone, System-Audio und lokale Apps brauchen einen stabilen, nachvollziehbaren Eingabepfad.',
      },
      {
        title: 'Nicht wie ein Bot klingen',
        description:
          'Stimme, Timing und Antworten müssen nah an echter Assistenz sein, nicht an einem Demo-Skript.',
      },
    ],
    solutions: [
      {
        title: 'Cancellation-aware Pipeline',
        description:
          'Audio-Verarbeitung und Agent-Antworten werden so gebaut, dass Unterbrechungen sauber verarbeitet werden.',
        tech: ['Swift Concurrency', 'Cancellation', 'Streaming'],
      },
      {
        title: 'Lokales Routing',
        description:
          'Klare Kontrolle darüber, welcher Audio-Eingang genutzt wird und welcher Flow gerade aktiv ist.',
        tech: ['CoreAudio', 'macOS', 'Runtime State'],
      },
      {
        title: 'Voice UX Polish',
        description:
          'Antwortlängen, Pausen und Verhalten werden als Produktoberfläche behandelt, nicht als Nebenprodukt.',
        tech: ['Voice UX', 'Prompting', 'Latency Tuning'],
      },
    ],
    results: [
      { metric: 'Flow', value: 'Realtime', description: 'Schnellere Gespräche mit Unterbrechungen' },
      { metric: 'Input', value: 'Robust', description: 'Stabilere Audio-Routen auf macOS' },
      { metric: 'UX', value: 'Natural', description: 'Weniger Bot-Gefühl im Alltag' },
    ],
    duration: 'laufend',
    teamSize: 'Founder-led',
    deliverables: ['Voice Runtime', 'Audio Routing', 'Interruption Handling', 'Voice UX'],
    nextProject: 'neon-core',
    prevProject: 'neowispr',
  },
  {
    id: 'neon-core',
    title: 'Agent OS mit Gateway & Memory',
    company: 'Neon Core',
    role: 'System Architect & Product Engineer',
    period: '2026 – Heute',
    description:
      'Neon-native Agent-Runtime mit Gateway, Memory-Kontext, Skills, Mission Control und echten geprüften Agent-Runs.',
    tech: ['TypeScript', 'Node.js', 'Discord Gateway', 'Memory', 'Mission Control'],
    industry: 'Neon Product Lab',
    current: true,
    overview:
      'Neon Core ist die technische Basis für echte Agent-Arbeit: Channels, Gateway, Memory, Skills, Policies, Mission Control und überprüfbare Runtime-Pfade statt isolierter Chat-Demos.',
    challenges: [
      {
        title: 'Echte Entry-Points',
        description:
          'Agenten müssen über Discord, UI, CLI und lokale Services laufen, nicht nur in isolierten Tests.',
      },
      {
        title: 'Memory als Produktkern',
        description:
          'Kontext darf nicht geraten werden. Er muss gefunden, bewertet und kontrolliert in Runs eingespeist werden.',
      },
      {
        title: 'Operator-Vertrauen',
        description:
          'Jeder Run braucht Status, Logs, Zustellung, Smoke-Pfade und klare Grenzen für riskante Aktionen.',
      },
    ],
    solutions: [
      {
        title: 'Gateway-first Architecture',
        description:
          'Channels, Geräte, Routing und Zustellung werden als erste Produktfläche behandelt.',
        tech: ['Node.js', 'Discord Gateway', 'HTTP APIs'],
      },
      {
        title: 'Memory Context Layer',
        description:
          'Projekt-, Personen- und Runtime-Kontext wird gezielt geladen und nicht blind in jeden Run geworfen.',
        tech: ['FTS', 'Semantic Memory', 'Context Scoring'],
      },
      {
        title: 'Mission Control',
        description:
          'Operator-Dashboard für Runs, Health, Zustellung und echte Runtime-Wahrheit.',
        tech: ['React', 'TypeScript', 'Live APIs'],
      },
    ],
    results: [
      { metric: 'Gateway', value: 'Live', description: 'Discord und lokale Runtime-Pfade geprüft' },
      { metric: 'Memory', value: '5000+', description: 'Langzeit-Einträge nutzbar im Workflow' },
      { metric: 'Quality', value: 'Typed', description: 'Strikte TypeScript- und Verify-Gates' },
    ],
    duration: 'laufend',
    teamSize: 'Neon SDK Team',
    deliverables: ['Gateway', 'Memory Context', 'Skills Runtime', 'Mission Control'],
    nextProject: 'systempiloten',
    prevProject: 'neovoice',
  },
  {
    id: 'systempiloten',
    title: 'KI-gestütztes CMS/CRM System',
    company: 'Systempiloten GmbH',
    role: 'Senior Fullstack Entwickler & UX Architekt',
    period: '11/2023 – Heute',
    description:
      'Entwicklung einer modularen Plattform für ein KI-gestütztes CMS/CRM mit integriertem Webbuilder. Architekturentscheidungen, Frontend- und Backend-Entwicklung, UX-Konzeption.',
    tech: ['React', 'Vue.js', 'Next.js', 'TypeScript', 'Tailwind CSS', 'Node.js', 'KI-Integration'],
    industry: 'Software & KI',
    current: true,
    overview:
      'Ein revolutionäres Content-Management-System, das KI-Technologie nutzt, um Website-Erstellung zu automatisieren. Nutzer können durch einfache Beschreibungen komplette Webseiten generieren, die automatisch responsive und SEO-optimiert sind.',
    challenges: [
      {
        title: 'KI-Integration',
        description:
          'Integration von Large Language Models zur automatischen Content-Generierung und Layout-Vorschlägen.',
      },
      {
        title: 'Drag & Drop Builder',
        description:
          'Entwicklung eines intuitiven Visual Builders mit Echtzeit-Vorschau und komplexen Verschachtelungsmöglichkeiten.',
      },
      {
        title: 'Performance bei großen Datensätzen',
        description:
          'Optimierung der Rendergeschwindigkeit bei Seiten mit hunderten von Komponenten und dynamischen Inhalten.',
      },
    ],
    solutions: [
      {
        title: 'Modulare KI-Pipeline',
        description:
          'Entwicklung einer flexiblen Pipeline, die verschiedene KI-Modelle für unterschiedliche Aufgaben einsetzt.',
        tech: ['OpenAI API', 'Custom Prompts', 'Streaming'],
      },
      {
        title: 'Virtual DOM für Builder',
        description:
          'Implementation eines eigenen virtuellen DOM-Systems für den Builder mit optimiertem Reconciliation.',
        tech: ['React', 'Custom Renderer', 'Web Workers'],
      },
      {
        title: 'Lazy Loading & Virtualisierung',
        description:
          'Intelligentes Laden von Komponenten mit Virtualisierung für große Listen und verschachtelte Strukturen.',
        tech: ['React Virtual', 'IntersectionObserver', 'Code Splitting'],
      },
    ],
    results: [
      { metric: 'Entwicklungszeit', value: '-60%', description: 'Für neue Websites durch KI-Unterstützung' },
      { metric: 'Performance Score', value: '95+', description: 'Lighthouse Score für generierte Seiten' },
      { metric: 'User Adoption', value: '+40%', description: 'Steigerung der aktiven Nutzer' },
    ],
    duration: '14+ Monate',
    teamSize: '8 Entwickler',
    deliverables: ['KI-Webbuilder', 'CRM-Modul', 'Analytics Dashboard', 'API-Schnittstellen'],
    nextProject: 'ceetrac',
    prevProject: 'neon-core',
  },
  {
    id: 'ceetrac',
    title: 'Nachhaltigkeits-Management Plattform',
    company: 'CEETRAC / CEEMAS',
    role: 'Lead Frontend Developer / UX/UI Designer',
    period: '11/2024 – 03/2025',
    description:
      'Komplettes Redesign und Frontend-Neuentwicklung der Nachhaltigkeits-Management-Plattform. Vue.js-Applikation mit Dashboard, Datenvisualisierung und Multi-User-System.',
    tech: ['Vue.js', 'TypeScript', 'Tailwind CSS', 'REST APIs', 'Dashboard'],
    url: 'ceetrac.de',
    industry: 'Energie',
    overview:
      'Eine umfassende Plattform für Unternehmen zur Erfassung, Analyse und Berichterstattung von Nachhaltigkeitsdaten gemäß EU-Taxonomie und CSRD-Richtlinien.',
    challenges: [
      {
        title: 'Komplexe Datenstrukturen',
        description:
          'Abbildung der EU-Taxonomie mit verschachtelten Kategorien, Aktivitäten und Kennzahlen.',
      },
      {
        title: 'Multi-Tenant Architektur',
        description:
          'Unterstützung für verschiedene Unternehmensstrukturen mit Konzern-, Tochter- und Standortebenen.',
      },
      {
        title: 'Datenvisualisierung',
        description:
          'Darstellung komplexer Nachhaltigkeitskennzahlen in verständlichen, interaktiven Charts.',
      },
    ],
    solutions: [
      {
        title: 'Rekursive Datenmodelle',
        description:
          'Flexible Datenstrukturen, die beliebig tiefe Hierarchien abbilden können.',
        tech: ['TypeScript Generics', 'Recursive Types', 'Vue Composables'],
      },
      {
        title: 'Role-Based Access Control',
        description:
          'Feingranulares Berechtigungssystem auf Basis von Rollen und Ressourcen.',
        tech: ['JWT', 'Vue Router Guards', 'Permission System'],
      },
      {
        title: 'Custom Chart Library',
        description:
          'Maßgeschneiderte Visualisierungskomponenten für Nachhaltigkeitsdaten.',
        tech: ['Chart.js', 'D3.js', 'Vue Components'],
      },
    ],
    results: [
      { metric: 'Dateneingabe', value: '-50%', description: 'Reduzierte Zeit für Nachhaltigkeitsreporting' },
      { metric: 'Nutzer-Zufriedenheit', value: '92%', description: 'In User-Befragungen' },
      { metric: 'Performance', value: '3x', description: 'Schnellere Ladezeiten vs. Vorgänger' },
    ],
    duration: '5 Monate',
    teamSize: '4 Entwickler',
    deliverables: ['Dashboard Redesign', 'Reporting Module', 'Admin Panel', 'API Integration'],
    nextProject: 'datasec',
    prevProject: 'systempiloten',
  },
  {
    id: 'datasec',
    title: 'Datensicherheits-Plattform',
    company: 'Datasec',
    role: 'React Frontend Developer',
    period: '11/2025 – 12/2025',
    description:
      'Entwicklung des kompletten Frontends für eine Datensicherheits-Plattform. Moderne, performante Single-Page-Application mit React und TypeScript.',
    tech: ['React', 'TypeScript', 'REST APIs', 'Modern CSS'],
    url: 'p-f7pgr7.project.space',
    industry: 'IT-Security',
    overview:
      'Eine Enterprise-Lösung für Datensicherheit und Compliance-Management. Die Plattform hilft Unternehmen, ihre Datenbestände zu klassifizieren, Risiken zu identifizieren und DSGVO-konform zu verwalten.',
    challenges: [
      {
        title: 'Sicherheits-First Design',
        description:
          'Implementierung von Security-Best-Practices bereits im Frontend.',
      },
      {
        title: 'Komplexe Workflows',
        description:
          'Abbildung mehrstufiger Genehmigungsprozesse und Audit-Trails.',
      },
      {
        title: 'Echtzeit-Updates',
        description:
          'Live-Aktualisierung von Sicherheitsstatus und Benachrichtigungen.',
      },
    ],
    solutions: [
      {
        title: 'Security Hardening',
        description:
          'XSS-Prevention, CSRF-Tokens, Content Security Policy im Frontend.',
        tech: ['React', 'Helmet', 'Sanitization'],
      },
      {
        title: 'State Machine Workflows',
        description:
          'Typsichere Workflow-Engine basierend auf State Machines.',
        tech: ['XState', 'TypeScript', 'React Context'],
      },
      {
        title: 'WebSocket Integration',
        description:
          'Bidirektionale Kommunikation für Echtzeit-Benachrichtigungen.',
        tech: ['Socket.io', 'React Query', 'Optimistic Updates'],
      },
    ],
    results: [
      { metric: 'Sicherheits-Score', value: 'A+', description: 'In Security Audits' },
      { metric: 'Time-to-Market', value: '6 Wochen', description: 'Vom Konzept zur Production' },
      { metric: 'Code Coverage', value: '85%', description: 'Unit & Integration Tests' },
    ],
    duration: '2 Monate',
    teamSize: '3 Entwickler',
    deliverables: ['Dashboard', 'Risk Assessment Module', 'Compliance Reports', 'User Management'],
    nextProject: 'levaco',
    prevProject: 'ceetrac',
  },
  {
    id: 'levaco',
    title: 'Corporate Website Redesign',
    company: 'Levaco Chemicals',
    role: 'Fullstack Developer / Lead UX/UI Designer',
    period: '10/2025 – 01/2026',
    description:
      'Komplettes Redesign und Neuentwicklung der Unternehmenswebsite. UX/UI Konzeption, WordPress-Entwicklung mit Elementor, Performance-Optimierung und SEO.',
    tech: ['WordPress', 'Elementor Pro', 'PHP', 'JavaScript', 'CSS'],
    url: 'levaco.wpcomstaging.com',
    industry: 'Chemie',
    overview:
      'Ein umfassendes Rebranding-Projekt für einen führenden Chemiespezialisten. Die neue Website präsentiert das Produktportfolio, stärkt die Arbeitgebermarke und optimiert die Lead-Generierung.',
    challenges: [
      {
        title: 'Produktkatalog Integration',
        description:
          'Dynamische Einbindung von über 500 chemischen Produkten mit Filterung und Suche.',
      },
      {
        title: 'Mehrsprachigkeit',
        description:
          'Internationaler Auftritt mit Deutsch und Englisch inklusive SEO-Optimierung.',
      },
      {
        title: 'Performance vs. Design',
        description:
          'Balance zwischen aufwendigen Animationen und schnellen Ladezeiten.',
      },
    ],
    solutions: [
      {
        title: 'Custom Product Taxonomy',
        description:
          'WordPress Custom Post Types mit erweiterten Metafeldern und Filterlogik.',
        tech: ['ACF Pro', 'Custom REST Endpoints', 'Vue.js Filter'],
      },
      {
        title: 'WPML Integration',
        description:
          'Vollständige Mehrsprachigkeit mit URL-Struktur und hreflang-Tags.',
        tech: ['WPML', 'Yoast SEO', 'Custom Scripts'],
      },
      {
        title: 'Progressive Loading',
        description:
          'Lazy Loading für Bilder, kritisches CSS inline, deferred JavaScript.',
        tech: ['WP Rocket', 'GSAP', 'IntersectionObserver'],
      },
    ],
    results: [
      { metric: 'Page Speed', value: '92/100', description: 'Google PageSpeed Score Mobile' },
      { metric: 'Organic Traffic', value: '+35%', description: 'Nach 3 Monaten' },
      { metric: 'Bounce Rate', value: '-22%', description: 'Verbesserung durch bessere UX' },
    ],
    duration: '4 Monate',
    teamSize: '2 Entwickler',
    deliverables: ['Responsive Website', 'Produktkatalog', 'Karriereportal', 'Kontaktformulare'],
    nextProject: 'mercedes',
    prevProject: 'datasec',
  },
  {
    id: 'mercedes',
    title: 'E-Commerce Shop Rollout',
    company: 'Mercedes-Benz AG',
    role: 'Technical Project Manager E-Commerce',
    period: '11/2022 – 04/2023',
    description:
      'Technische Leitung des Online-Shop Rollouts für Mercedes-Benz. API-Schnittstellenmanagement und Koordination internationaler Entwicklungsteams.',
    tech: ['REST APIs', 'SAP Commerce', 'Jira', 'Confluence', 'Agile/Scrum'],
    industry: 'Automotive',
    overview:
      'Koordination des internationalen Rollouts der Mercedes-Benz E-Commerce Plattform. Verantwortlich für API-Integration, Qualitätssicherung und teamübergreifende Kommunikation.',
    challenges: [
      {
        title: 'Internationale Teams',
        description:
          'Koordination von Entwicklungsteams in verschiedenen Zeitzonen und Kulturen.',
      },
      {
        title: 'API-Komplexität',
        description:
          'Integration von SAP Commerce mit Legacy-Systemen und Drittanbietern.',
      },
      {
        title: 'Qualitätsstandards',
        description:
          'Einhaltung der Mercedes-Benz Brand Guidelines und technischen Standards.',
      },
    ],
    solutions: [
      {
        title: 'Agile Framework',
        description:
          'Etablierung von Scrum mit angepassten Ritualen für verteilte Teams.',
        tech: ['Jira', 'Confluence', 'Slack'],
      },
      {
        title: 'API-Gateway',
        description:
          'Zentrales Gateway zur Harmonisierung verschiedener API-Versionen.',
        tech: ['REST', 'GraphQL Gateway', 'API Documentation'],
      },
      {
        title: 'Automatisierte QA',
        description:
          'CI/CD Pipeline mit automatisierten Tests und Brand Compliance Checks.',
        tech: ['Jenkins', 'Selenium', 'Custom Linting'],
      },
    ],
    results: [
      { metric: 'Rollout', value: '5 Märkte', description: 'Erfolgreich gelauncht' },
      { metric: 'Defect Rate', value: '-40%', description: 'Durch automatisierte Tests' },
      { metric: 'Time-to-Deploy', value: '-30%', description: 'Durch CI/CD Optimierung' },
    ],
    duration: '6 Monate',
    teamSize: '15+ Entwickler',
    deliverables: ['API Integration', 'QA Framework', 'Documentation', 'Training'],
    nextProject: 'eon',
    prevProject: 'levaco',
  },
  {
    id: 'eon',
    title: 'Kunden-Portal Optimierung',
    company: 'E.ON Energie Deutschland GmbH',
    role: 'UX Designer',
    period: '10/2021 – 01/2022',
    description:
      'Konzeption und Optimierung von Userflows im Energieumfeld. Überarbeitung von Header-Strukturen, Entwicklung neuer Solar-Features und Optimierung digitaler Produktberater.',
    tech: ['Figma', 'Sketch', 'Jira', 'Zeplin'],
    industry: 'Energie',
    overview:
      'Konzeption und Optimierung von Userflows im Energieumfeld. Fokus auf Usability, Conversion und klare Nutzerführung.',
    challenges: [
      {
        title: 'Legacy Codebase',
        description:
          'Integration von modernen UI-Komponenten in bestehendes jQuery-basiertes System.',
      },
      {
        title: 'Conversion-Probleme',
        description:
          'Hohe Absprungrate im Registrierungs- und Anmeldeprozess.',
      },
      {
        title: 'Accessibility',
        description:
          'Erfüllung der WCAG 2.1 AA Standards für öffentliche Versorgungsunternehmen.',
      },
    ],
    solutions: [
      {
        title: 'Progressive Enhancement',
        description:
          'Schrittweise Modernisierung ohne vollständigen Rewrite.',
        tech: ['Vanilla JS', 'CSS Custom Properties', 'Intersection Observer'],
      },
      {
        title: 'A/B Testing',
        description:
          'Datengetriebene Optimierung mit Multi-Variant Tests.',
        tech: ['Google Optimize', 'Analytics Events', 'Heatmaps'],
      },
      {
        title: 'Accessibility Audit',
        description:
          'Systematische Überprüfung und Anpassung aller Formulare und Interaktionen.',
        tech: ['NVDA/JAWS Testing', 'Axe DevTools', 'Manual Audits'],
      },
    ],
    results: [
      { metric: 'Conversion Rate', value: '+18%', description: 'Im Anmeldeprozess' },
      { metric: 'Accessibility', value: 'AA', description: 'WCAG 2.1 Konformität' },
      { metric: 'Support Tickets', value: '-25%', description: 'Durch verbesserte Self-Service' },
    ],
    duration: '4 Monate',
    teamSize: '6 Entwickler',
    deliverables: ['Optimierte Anmeldestrecke', 'Accessibility Report', 'Style Guide', 'Testing Suite'],
    nextProject: 'valsight',
    prevProject: 'mercedes',
  },
  {
    id: 'valsight',
    title: 'KI Business Intelligence UI',
    company: 'Valsight GmbH',
    role: 'UX/UI Designer',
    period: '09/2021 – 02/2022',
    description:
      'Gestaltung und Konzeption des UX/UI-Designs einer KI-basierten Business-Intelligence-Software zur Analyse von Umsatztrends und Risikoerkennung.',
    tech: ['Figma', 'Adobe XD', 'HTML', 'CSS', 'JavaScript'],
    industry: 'Software & KI',
    overview:
      'Gestaltung und Konzeption des UX/UI-Designs einer KI-basierten Business-Intelligence-Software. Ziel war die Entwicklung einer intuitiven Oberfläche zur schnellen Entscheidungsfindung.',
    challenges: [
      {
        title: 'Komplexe Datenmengen',
        description:
          'Visualisierung von Millionen von Datenpunkten ohne Performance-Einbußen.',
      },
      {
        title: 'Scenario Planning UI',
        description:
          'Intuitive Oberfläche für What-If-Analysen und Simulationen.',
      },
      {
        title: 'Enterprise Design System',
        description:
          'Integration in bestehendes Design System mit strikten Vorgaben.',
      },
    ],
    solutions: [
      {
        title: 'Canvas Rendering',
        description:
          'Hochperformante Visualisierung durch direktes Canvas-Rendering.',
        tech: ['Canvas API', 'OffscreenCanvas', 'RequestAnimationFrame'],
      },
      {
        title: 'Scenario Trees',
        description:
          'Visuelle Darstellung von Entscheidungsbäumen mit Drag-and-Drop.',
        tech: ['D3.js', 'React DnD', 'Custom Layouts'],
      },
      {
        title: 'Component Library',
        description:
          'Erweiterung der bestehenden Component Library mit neuen Chart-Types.',
        tech: ['Storybook', 'TypeScript', 'Design Tokens'],
      },
    ],
    results: [
      { metric: 'Render Performance', value: '60fps', description: 'Bei 1M+ Datenpunkten' },
      { metric: 'User Engagement', value: '+35%', description: 'Zeit in der App' },
      { metric: 'Components', value: '25+', description: 'Neue UI-Komponenten' },
    ],
    duration: '6 Monate',
    teamSize: '4 Entwickler',
    deliverables: ['Dashboard Components', 'Chart Library', 'Scenario Editor', 'Documentation'],
    nextProject: 'neowispr',
    prevProject: 'eon',
  },
];

// Helper function to get project by ID
export function getProjectById(id: string): NikoProjectExtended | undefined {
  return projectsDetailData.find((p) => p.id === id);
}

// Helper function to get adjacent projects
export function getAdjacentProjects(id: string): {
  prev: NikoProjectExtended | undefined;
  next: NikoProjectExtended | undefined;
} {
  const project = getProjectById(id);
  if (!project) return { prev: undefined, next: undefined };

  return {
    prev: project.prevProject ? getProjectById(project.prevProject) : undefined,
    next: project.nextProject ? getProjectById(project.nextProject) : undefined,
  };
}
